import {
	Capacitor,
	CapacitorHttp,
	type HttpResponse,
} from '../utils/capacitorShim';
import { ActivityIdService } from './ActivityIdService';
import { AuthService } from './AuthService';
import ConfigService from './ConfigService';
import { getViteEnv, isViteHot } from './runtimeEnv';

export interface ApiRequestOptions {
	headers?: Record<string, string>;
	timeout?: number;
	signal?: AbortSignal;
}

import type { StreamEvent } from '../types/api';

export interface ApiError {
	status: number;
	message: string;
	data?: any;
}

export interface StreamOptions extends ApiRequestOptions {
	onMessage?: (event: StreamEvent) => void;
	onError?: (error: Error) => void;
	onComplete?: () => void;
}

// Type for the config from environment
interface AppConfigExtra {
	apiUrl: string;
	apiTimeout: number;
	apiRetryAttempts: number;
	debug: boolean;
	environment: 'development' | 'production';
	apiKey?: string;
}

// Window configuration for runtime web config
interface WindowConfig extends AppConfigExtra {
	apiKey?: string;
}

declare global {
	// noinspection JSUnusedGlobalSymbols
	interface Window {
		__APP_CONFIG__?: WindowConfig;
	}
}

/**
 * Shared API Service for making HTTP requests
 */
class ApiServiceClass {
	private _baseUrl: string;
	private readonly _timeout: number;
	private readonly _retryAttempts: number;

	private _getEnvApiUrl(): string | null {
		const viteUrl = getViteEnv('VITE_API_URL');
		if (viteUrl) return viteUrl;

		if (typeof process !== 'undefined' && process.env?.API_URL) {
			return process.env.API_URL;
		}

		return null;
	}

	private _getAuthServerUrl(): string {
		const viteUrl = getViteEnv('VITE_AUTH_SERVER_URL');
		if (viteUrl) return viteUrl;

		if (typeof process !== 'undefined' && process.env?.AUTH_SERVER_URL) {
			return process.env.AUTH_SERVER_URL;
		}

		throw new Error(
			'AUTH_SERVER_URL not configured. Set VITE_AUTH_SERVER_URL environment variable.',
		);
	}

	private _getClientId(): string {
		const viteId = getViteEnv('VITE_OAUTH_CLIENT_ID');
		if (viteId) return viteId;

		if (typeof process !== 'undefined' && process.env?.OAUTH_CLIENT_ID) {
			return process.env.OAUTH_CLIENT_ID;
		}

		throw new Error(
			'OAUTH_CLIENT_ID not configured. Set VITE_OAUTH_CLIENT_ID environment variable.',
		);
	}

	public constructor() {
		// Check for runtime web configuration first
		let config: AppConfigExtra | undefined;

		if (typeof window !== 'undefined' && window.__APP_CONFIG__) {
			config = window.__APP_CONFIG__;
		} else {
			const getEnvVar = (
				viteKey: string,
				...fallbackKeys: string[]
			): string | undefined => {
				const viteVal = getViteEnv(viteKey);
				if (viteVal) return viteVal;

				if (typeof process !== 'undefined' && process.env) {
					for (const key of fallbackKeys) {
						if (process.env[key]) {
							return process.env[key];
						}
					}
				}
				return undefined;
			};

			const apiKey = getEnvVar(
				'VITE_API_KEY',
				'REACT_APP_API_KEY',
				'NX_API_KEY',
			);
			config = {
				apiUrl: getEnvVar('VITE_API_URL', 'REACT_APP_API_URL', 'NX_API_URL'),
				apiTimeout: parseInt(
					getEnvVar(
						'VITE_API_TIMEOUT',
						'REACT_APP_API_TIMEOUT',
						'NX_API_TIMEOUT',
					) || '60000',
					10,
				),
				apiRetryAttempts: parseInt(
					getEnvVar(
						'VITE_API_RETRY_ATTEMPTS',
						'REACT_APP_API_RETRY_ATTEMPTS',
						'NX_API_RETRY_ATTEMPTS',
					) || '3',
					10,
				),
				debug:
					getEnvVar('VITE_DEBUG', 'REACT_APP_DEBUG', 'NX_DEBUG') === 'true',
				environment: (getEnvVar(
					'VITE_ENVIRONMENT',
					'REACT_APP_ENVIRONMENT',
					'NX_ENVIRONMENT',
				) || 'development') as 'development' | 'production',
				...(apiKey ? { apiKey } : {}),
			};
		}

		if (!config?.apiUrl) {
			throw new Error(
				'API URL not configured. Set VITE_API_URL environment variable.',
			);
		}
		this._baseUrl = config.apiUrl;

		// Also set it in ConfigService for other services to use
		ConfigService.getInstance().setApiBaseUrl(config.apiUrl);
		this._timeout = config?.apiTimeout || 60000;
		this._retryAttempts = config?.apiRetryAttempts || 3;
	}

	private async _getDefaultHeaders(): Promise<Record<string, string>> {
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
		};

		// Add user's timezone offset header (in minutes)
		try {
			const offsetMinutes = new Date().getTimezoneOffset();
			headers['X-TZ-Offset'] = offsetMinutes.toString();
		} catch (error) {
			// Timezone detection not supported
		}

		// Tag every request with the current activity id + description so
		// the backend can dedupe access-log writes on a per-navigation basis
		// and store a meaningful row label (e.g. "Viewed Clients") instead
		// of a URL-derived guess. Both are regenerated on every route change
		// by the router-level hook.
		try {
			const activityService = ActivityIdService.getInstance();
			headers['X-Activity-Id'] = activityService.getCurrent();

			const activityDescription = activityService.getCurrentDescription();
			if (activityDescription != null) {
				headers['X-Activity-Description'] = activityDescription;
			}
		} catch (error) {
			// Activity-id generation should never throw, but if it does we
			// just skip the header and the backend logs every request.
		}

		// Get authentication header from AuthService
		try {
			// Get AuthService instance - it should already be initialized by App.tsx
			const authServerUrl = this._getAuthServerUrl();
			const clientId = this._getClientId();
			const authService = AuthService.getInstance(authServerUrl, clientId);

			const authState = authService.getAuthState();
			const authHeader = authService.getAuthHeader();

			if (Capacitor.isNativePlatform()) {
				console.debug(
					'[ApiService] isAuthenticated:',
					authState.isAuthenticated,
				);
				console.debug(
					'[ApiService] hasAccessToken:',
					authState.accessToken != null,
				);
				console.debug(
					'[ApiService] authHeader:',
					authHeader != null
						? `${authHeader.name}: ${authHeader.value.substring(0, 20)}...`
						: 'null',
				);
			}

			if (authHeader) {
				headers[authHeader.name] = authHeader.value;
			}
		} catch (error) {
			console.debug('[ApiService] Error getting auth header:', error);
		}

		return headers;
	}

	private async _makeRequest<T = any>(
		method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
		endpoint: string,
		body?: any,
		options?: ApiRequestOptions,
		isRetry: boolean = false,
	): Promise<T> {
		// Check if baseUrl needs updating (for HMR issues in development)
		if (typeof window !== 'undefined' && isViteHot()) {
			const envUrl = this._getEnvApiUrl();
			if (envUrl && envUrl !== this._baseUrl) {
				this._baseUrl = envUrl;
			}
		}

		const url = `${this._baseUrl}${endpoint}`;

		if (Capacitor.isNativePlatform()) {
			console.debug('[ApiService] Request:', method, url);
		}

		const defaultHeaders = await this._getDefaultHeaders();
		const headers = {
			...defaultHeaders,
			...options?.headers,
		};

		// Use CapacitorHttp on native platforms to avoid WebView networking issues
		if (Capacitor.isNativePlatform()) {
			return this._makeNativeRequest<T>(
				method,
				url,
				headers,
				body,
				endpoint,
				options,
				isRetry,
			);
		}

		// Web platform: use regular fetch
		const config: RequestInit = {
			method,
			headers,
		};

		if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
			if (body instanceof FormData) {
				config.body = body;
				delete headers['Content-Type'];
			} else {
				config.body = JSON.stringify(body);
			}
		}

		// Use provided signal or create a new one for timeout
		let timeoutId: NodeJS.Timeout | undefined;

		if (options?.signal) {
			// Use the provided signal
			config.signal = options.signal;
		} else {
			// Create our own controller for timeout
			const requestTimeout = options?.timeout || this._timeout;
			const controller = new AbortController();
			timeoutId = setTimeout(() => {
				controller.abort();
			}, requestTimeout);
			config.signal = controller.signal;
		}

		try {
			const response = await fetch(url, config);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			// Handle 401 Unauthorized
			if (response.status === 401 && !isRetry) {
				try {
					const authService = AuthService.getInstance();

					// Try to refresh the token
					const refreshed = await authService.refreshAccessToken();

					if (refreshed) {
						// Retry the request with the new token
						return this._makeRequest<T>(method, endpoint, body, options, true);
					} else {
						// Token refresh failed, trigger logout
						const currentUrl =
							typeof window !== 'undefined' ? window.location.href : undefined;
						await authService.handleAuthFailure(currentUrl);
						throw new Error('Authentication failed');
					}
				} catch (error) {
					// AuthService not available or refresh failed
					throw new Error('Authentication failed');
				}
			}

			return await this._handleResponse<T>(response);
		} catch (error) {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			throw error;
		}
	}

	/**
	 * Make HTTP request using CapacitorHttp for native platforms.
	 * This bypasses WebView networking issues on iOS/Android.
	 */
	private async _makeNativeRequest<T = any>(
		method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
		url: string,
		headers: Record<string, string>,
		body?: unknown,
		endpoint?: string,
		options?: ApiRequestOptions,
		isRetry: boolean = false,
	): Promise<T> {
		try {
			const requestOptions: {
				url: string;
				method: string;
				headers: Record<string, string>;
				data?: unknown;
				connectTimeout?: number;
				readTimeout?: number;
			} = {
				url,
				method,
				headers,
			};

			// Add body for POST/PUT/PATCH
			if (
				body &&
				(method === 'POST' || method === 'PUT' || method === 'PATCH')
			) {
				if (body instanceof FormData) {
					// FormData not supported by CapacitorHttp - fall back to fetch
					console.debug(
						'[ApiService] FormData detected, falling back to fetch',
					);
					return this._makeFetchRequest<T>(
						method,
						url,
						headers,
						body,
						endpoint,
						options,
						isRetry,
					);
				}
				requestOptions.data = body;
			}

			// Set timeout
			const timeout = options?.timeout ?? this._timeout;
			requestOptions.connectTimeout = timeout;
			requestOptions.readTimeout = timeout;

			const result = await CapacitorHttp.request(requestOptions);

			console.debug('[ApiService] Native response:', result.status);

			// Handle 401 Unauthorized
			if (result.status === 401 && !isRetry) {
				try {
					const authService = AuthService.getInstance();
					const refreshed = await authService.refreshAccessToken();

					if (refreshed && endpoint != null) {
						return this._makeRequest<T>(method, endpoint, body, options, true);
					} else {
						const currentUrl =
							typeof window !== 'undefined' ? window.location.href : undefined;
						await authService.handleAuthFailure(currentUrl);
						throw new Error('Authentication failed');
					}
				} catch {
					throw new Error('Authentication failed');
				}
			}

			return this._handleNativeResponse<T>(result);
		} catch (error) {
			const err = error as Error;
			console.debug(
				'[ApiService] Native request error:',
				err?.name,
				err?.message,
			);
			throw error;
		}
	}

	/**
	 * Fallback to regular fetch for cases CapacitorHttp doesn't support (like FormData)
	 */
	private async _makeFetchRequest<T = any>(
		method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
		url: string,
		headers: Record<string, string>,
		body?: unknown,
		endpoint?: string,
		options?: ApiRequestOptions,
		isRetry: boolean = false,
	): Promise<T> {
		const config: RequestInit = {
			method,
			headers,
		};

		if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
			if (body instanceof FormData) {
				config.body = body;
				delete headers['Content-Type'];
			} else {
				config.body = JSON.stringify(body as object);
			}
		}

		const response = await fetch(url, config);

		if (response.status === 401 && !isRetry && endpoint != null) {
			try {
				const authService = AuthService.getInstance();
				const refreshed = await authService.refreshAccessToken();

				if (refreshed) {
					return this._makeRequest<T>(method, endpoint, body, options, true);
				}
			} catch {
				// Ignore
			}
			throw new Error('Authentication failed');
		}

		return await this._handleResponse<T>(response);
	}

	/**
	 * Handle CapacitorHttp response format
	 */
	private _handleNativeResponse<T>(result: HttpResponse): T {
		const { status, data, headers } = result;

		// Success responses
		if (status >= 200 && status < 300) {
			// Handle empty responses
			if (status === 204 || data == null) {
				return null as T;
			}

			// CapacitorHttp automatically parses JSON, so data is already parsed
			return data as T;
		}

		// Error responses
		let errorMessage = `Request failed with status ${status}`;

		if (status === 403) {
			errorMessage =
				'Access denied. You do not have permission to perform this action.';
		} else if (status === 404) {
			errorMessage = 'The requested resource was not found.';
		} else if (status === 500) {
			errorMessage =
				'An internal server error occurred. Please try again later.';
		} else if (status === 502 || status === 503) {
			errorMessage = 'Service temporarily unavailable. Please try again later.';
		}

		// Try to get error message from response body
		if (data != null && typeof data === 'object') {
			if (status !== 403 && (data as Record<string, unknown>).message) {
				errorMessage = (data as Record<string, unknown>).message as string;
			} else if (status !== 403 && (data as Record<string, unknown>).error) {
				errorMessage = (data as Record<string, unknown>).error as string;
			} else if (
				status === 403 &&
				((data as Record<string, unknown>).message ??
					(data as Record<string, unknown>).error)
			) {
				const serverMessage = ((data as Record<string, unknown>).message ??
					(data as Record<string, unknown>).error) as string;
				errorMessage = `Access denied: ${serverMessage}`;
			}
		}

		const apiError: ApiError = {
			status,
			message: errorMessage,
			data,
		};

		const error = new Error(errorMessage) as Error & { apiError?: ApiError };
		error.apiError = apiError;
		throw error;
	}

	private async _handleResponse<T>(response: Response): Promise<T> {
		if (response.ok) {
			// Check if response has content
			const contentType = response.headers.get('content-type');
			const contentLength = response.headers.get('content-length');

			// Handle empty responses (204 No Content or empty body)
			if (response.status === 204 || contentLength === '0') {
				return null as any as T;
			}

			// Handle non-JSON responses
			if (contentType && !contentType.includes('application/json')) {
				const text = await response.text();
				return text as any as T;
			}

			// Handle JSON responses
			try {
				const text = await response.text();
				// Return null for empty response body
				if (!text) {
					return null as any as T;
				}
				// Parse JSON
				return JSON.parse(text);
			} catch (parseError) {
				// Re-throw AbortError so it can be handled by caller
				if (parseError instanceof Error && parseError.name === 'AbortError') {
					throw parseError;
				}

				throw new Error(
					`Invalid JSON response: ${parseError instanceof Error ? parseError.message : String(parseError)}`,
				);
			}
		}

		// Handle error responses
		let errorMessage = `Request failed with status ${response.status}`;
		let errorData: any = null;

		// Provide user-friendly messages for common HTTP status codes
		if (response.status === 403) {
			errorMessage =
				'Access denied. You do not have permission to perform this action.';
		} else if (response.status === 404) {
			errorMessage = 'The requested resource was not found.';
		} else if (response.status === 500) {
			errorMessage =
				'An internal server error occurred. Please try again later.';
		} else if (response.status === 502 || response.status === 503) {
			errorMessage = 'Service temporarily unavailable. Please try again later.';
		}

		try {
			errorData = await response.json();
			// Use server-provided message if available, but keep our user-friendly 403 message
			if (response.status === 403) {
				// For 403, append additional context if server provides it
				if (errorData.message || errorData.error) {
					const serverMessage = errorData.message || errorData.error;
					errorMessage = `Access denied: ${serverMessage}`;
				}
			} else if (response.status === 500) {
				// For 500 errors, prefer 'error' field as 'message' is often generic
				if (errorData.error) {
					errorMessage = errorData.error;
				} else if (
					errorData.message &&
					errorData.message !== 'An error occurred'
				) {
					errorMessage = errorData.message;
				}
			} else if (errorData.message) {
				errorMessage = errorData.message;
			} else if (errorData.error) {
				errorMessage = errorData.error;
			}
		} catch {
			// If error response is not JSON, use our user-friendly message or status text
			if (
				response.status !== 403 &&
				response.status !== 404 &&
				response.status !== 500
			) {
				errorMessage = response.statusText || errorMessage;
			}
		}

		// Create structured error for other cases
		const apiError: ApiError = {
			status: response.status,
			message: errorMessage,
			data: errorData,
		};

		const error = new Error(errorMessage) as Error & { apiError?: ApiError };
		error.apiError = apiError;
		throw error;
	}

	// GET request
	public async apiGet<T = unknown>(
		endpoint: string,
		params?: Record<
			string,
			string | number | boolean | object | undefined | null
		>,
		options?: ApiRequestOptions,
	): Promise<T> {
		let url = endpoint;

		if (params != null) {
			const searchParams = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				if (value != null) {
					if (Array.isArray(value)) {
						if (value.length > 0 && typeof value[0] === 'object') {
							searchParams.append(key, JSON.stringify(value));
						} else {
							value.forEach((item) => {
								searchParams.append(key, item.toString());
							});
						}
					} else if (typeof value === 'object') {
						searchParams.append(key, JSON.stringify(value));
					} else {
						searchParams.append(key, value.toString());
					}
				}
			});

			if (searchParams.toString() !== '') {
				const separator = url.includes('?') ? '&' : '?';
				url += `${separator}${searchParams.toString()}`;
			}
		}

		return this._makeRequest<T>('GET', url, undefined, options);
	}

	// POST request
	public async apiPost<TRequestModel = any, TResponseModel = any>(
		endpoint: string,
		body?: TRequestModel,
		options?: ApiRequestOptions,
	): Promise<TResponseModel>;
	public async apiPost<TResponseModel = any>(
		endpoint: string,
		body?: any,
		options?: ApiRequestOptions,
	): Promise<TResponseModel>;
	public async apiPost<TResponseModel = any>(
		endpoint: string,
		body?: any,
		options?: ApiRequestOptions,
	): Promise<TResponseModel> {
		return this._makeRequest<TResponseModel>('POST', endpoint, body, options);
	}

	// PUT request
	public async apiPut<T = any>(
		endpoint: string,
		body?: any,
		options?: ApiRequestOptions,
	): Promise<T> {
		return this._makeRequest<T>('PUT', endpoint, body, options);
	}

	// PATCH request
	public async apiPatch<T = any>(
		endpoint: string,
		body?: any,
		options?: ApiRequestOptions,
	): Promise<T> {
		return this._makeRequest<T>('PATCH', endpoint, body, options);
	}

	// DELETE request
	public async apiDelete<T = any>(
		endpoint: string,
		options?: ApiRequestOptions,
	): Promise<T> {
		return this._makeRequest<T>('DELETE', endpoint, undefined, options);
	}

	// GET request for blob/file downloads
	public async apiGetBlob(
		endpoint: string,
		params?: Record<string, string | number | boolean>,
		options?: ApiRequestOptions,
	): Promise<Blob> {
		let url = endpoint;

		if (params) {
			const searchParams = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					searchParams.append(key, value.toString());
				}
			});

			if (searchParams.toString()) {
				const separator = url.includes('?') ? '&' : '?';
				url += `${separator}${searchParams.toString()}`;
			}
		}

		const fullUrl = `${this._baseUrl}${url}`;
		const defaultHeaders = await this._getDefaultHeaders();
		const headers = {
			...defaultHeaders,
			...options?.headers,
		};

		// Remove Content-Type for blob requests
		delete headers['Content-Type'];

		const config: RequestInit = {
			method: 'GET',
			headers,
		};

		// Use timeout from options or instance default
		const requestTimeout = options?.timeout || this._timeout;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => {
			controller.abort();
		}, requestTimeout);
		config.signal = controller.signal;

		try {
			const response = await fetch(fullUrl, config);
			clearTimeout(timeoutId);

			// Handle 401 Unauthorized
			if (response.status === 401) {
				try {
					const authService = AuthService.getInstance();

					// Try to refresh the token
					const refreshed = await authService.refreshAccessToken();

					if (refreshed) {
						// Retry the request with the new token
						return this.apiGetBlob(endpoint, params, options);
					} else {
						// Token refresh failed, trigger logout
						const currentUrl =
							typeof window !== 'undefined' ? window.location.href : undefined;
						await authService.handleAuthFailure(currentUrl);
						throw new Error('Authentication failed');
					}
				} catch (error) {
					// AuthService not available or refresh failed
					console.error('ApiService.apiGetBlob: Auth handling failed:', error);
					throw new Error('Authentication failed');
				}
			}

			if (!response.ok) {
				console.error(
					'ApiService.apiGetBlob: Response not OK:',
					response.status,
					response.statusText,
				);

				// Try to get error details from response body
				let errorDetails = '';
				try {
					const errorText = await response.text();
					console.error(
						'ApiService.apiGetBlob: Error response body:',
						errorText,
					);
					errorDetails = errorText ? ` - ${errorText}` : '';
				} catch (e) {
					console.error(
						'ApiService.apiGetBlob: Could not read error response body:',
						e,
					);
				}

				throw new Error(
					`Failed to download file: ${response.status} ${response.statusText}${errorDetails}`,
				);
			}

			const blob = await response.blob();

			return blob;
		} catch (error) {
			clearTimeout(timeoutId);
			throw error;
		}
	}

	// SSE streaming request
	public async streamPost(
		endpoint: string,
		body?: any,
		options?: StreamOptions,
	): Promise<EventSource> {
		const url = `${this._baseUrl}${endpoint}`;

		// For SSE, we need to use EventSource which doesn't support POST directly
		// So we'll use a workaround with fetch for the initial connection
		// and return a custom EventSource-like object

		return new Promise(async (resolve, reject) => {
			// Create headers with auth
			const defaultHeaders = await this._getDefaultHeaders();
			const headers = {
				...defaultHeaders,
				...options?.headers,
				Accept: 'text/event-stream',
				'Cache-Control': 'no-cache',
			};

			// Since EventSource doesn't support POST with body, we'll use fetch with ReadableStream
			fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(body),
				credentials: 'include',
			})
				.then(async (response) => {
					if (!response.ok) {
						// Handle auth failures
						if (response.status === 401) {
							try {
								const authService = AuthService.getInstance();
								const refreshed = await authService.refreshAccessToken();

								if (refreshed) {
									// Retry with new token
									return this.streamPost(endpoint, body, options);
								}
							} catch (error) {
								// Auth refresh failed
							}
						}
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					if (!response.body) {
						throw new Error('Response body is null');
					}

					const reader = response.body.getReader();
					const decoder = new TextDecoder();
					let buffer = '';

					// Create a custom EventSource-like object
					let readyState = 1; // OPEN
					const eventSource = {
						get readyState() {
							return readyState;
						},
						close: () => {
							reader.cancel();
							readyState = 2; // CLOSED
						},
						addEventListener: (type: string, listener: any) => {
							// Not implemented for simplicity
						},
						removeEventListener: (type: string, listener: any) => {
							// Not implemented for simplicity
						},
						url,
						withCredentials: true,
					} as EventSource;

					// Start reading the stream
					const processStream = async () => {
						try {
							while (true) {
								const { done, value } = await reader.read();

								if (done) {
									options?.onComplete?.();
									break;
								}

								buffer += decoder.decode(value, { stream: true });
								const lines = buffer.split('\n');
								buffer = lines.pop() || '';

								for (const line of lines) {
									if (line.startsWith('data: ')) {
										const data = line.slice(6);
										if (data === '[DONE]') {
											options?.onComplete?.();
											continue;
										}

										try {
											const event = JSON.parse(data) as StreamEvent;
											options?.onMessage?.(event);
										} catch (e) {
											// Warning: Failed to parse SSE data
										}
									}
								}
							}
						} catch (error) {
							options?.onError?.(error as Error);
						}
					};

					processStream();
					resolve(eventSource);
				})
				.catch((error) => {
					options?.onError?.(error);
					reject(error);
				});
		});
	}

	// Get current base URL
	public getBaseUrl(): string {
		return this._baseUrl;
	}
}

// Utility function to check if an error has API error information
export function hasApiError(
	error: any,
): error is Error & { apiError: ApiError } {
	return (
		error &&
		typeof error.apiError === 'object' &&
		typeof error.apiError.status === 'number'
	);
}

// Utility function to get user-friendly error message from API errors
export function getApiErrorMessage(error: any): string {
	// Check if it's an API error with structured information
	if (hasApiError(error)) {
		return error.apiError.message;
	}

	// Check if it's a regular Error object
	if (error instanceof Error) {
		return error.message;
	}

	// Check if error has a message property
	if (error && typeof error.message === 'string') {
		return error.message;
	}

	// Default fallback message
	return 'An unexpected error occurred. Please try again.';
}

// Use global window object to ensure true singleton across chunks
declare global {
	// noinspection JSUnusedGlobalSymbols
	interface Window {
		__API_SERVICE_INSTANCE__?: ApiServiceClass;
	}
}

// Create a true singleton that works across code-split chunks
function getApiServiceInstance(): ApiServiceClass {
	if (typeof window !== 'undefined') {
		if (!window.__API_SERVICE_INSTANCE__) {
			window.__API_SERVICE_INSTANCE__ = new ApiServiceClass();
		}
		return window.__API_SERVICE_INSTANCE__;
	} else {
		// For SSR or Node.js environments
		const globalAny = global as any;
		if (!globalAny.__API_SERVICE_INSTANCE__) {
			globalAny.__API_SERVICE_INSTANCE__ = new ApiServiceClass();
		}
		return globalAny.__API_SERVICE_INSTANCE__;
	}
}

// Export the singleton getter - lazy initialization
export const ApiService = new Proxy({} as ApiServiceClass, {
	get(target, prop, receiver) {
		const instance = getApiServiceInstance();
		return Reflect.get(instance, prop, instance);
	},
});
